import type { ConnectionView, ConnectionsCapabilities } from "../../state/connections";
import { Button } from "../ui/button";
import { ConnectorIcon } from "./ConnectorIcon";
import { PluginAccountButtons } from "./PluginAccountActions";
import { getConnectedAccountLabel, getPluginAccountState } from "./pluginContent";
import type { PluginAccountActions } from "./usePluginConnections";

export interface PluginRowActions extends PluginAccountActions {
  readonly onOpen: (connectionId: string) => void;
}

export function PluginRow({
  connection,
  capabilities,
  actions,
}: {
  readonly connection: ConnectionView;
  readonly capabilities: ConnectionsCapabilities;
  readonly actions: PluginRowActions;
}) {
  const state = getPluginAccountState(connection, capabilities);
  const accountLabel = getConnectedAccountLabel(connection);
  return (
    // One control, not a row that is also a button: the ghost Button is the
    // row — its own hover and press, its own padding — and the account action
    // sits beside it as a sibling, never inside it.
    <div className="relative -mx-3 flex min-w-0 items-center">
      <Button
        variant="ghost"
        className="h-auto min-w-0 flex-1 justify-start gap-3 px-3 py-2.5 text-left font-normal whitespace-normal sm:h-auto"
        onClick={() => actions.onOpen(connection.id)}
      >
        <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-background p-2">
          <ConnectorIcon connection={connection} />
        </span>
        <span className="min-w-0 flex-1 pr-28">
          <span className="block truncate text-sm font-medium text-foreground">
            {connection.name}
          </span>
          <span className="block truncate text-sm text-muted-foreground">
            {state.connected
              ? (accountLabel ?? "Connected")
              : state.needsReconnect
                ? state.label
                : connection.description}
          </span>
        </span>
      </Button>
      <div className="absolute top-1/2 right-3 -translate-y-1/2">
        <PluginAccountButtons connection={connection} capabilities={capabilities} actions={actions} />
      </div>
    </div>
  );
}
